// Blade — a reusable right-side slide-out overlay panel (Azure-portal "blade"),
// the app's standard alternative to a centered modal Dialog for richer, in-place
// editing/detail surfaces where the user should keep their place behind it.
//
// Owns the chrome only: backdrop (click-to-close), the fixed right panel with a
// slide-in transform, Escape-to-close, a header (optional icon + title +
// optional subtitle + close button), a scrollable body (children), and an
// optional pinned footer action bar. Content is entirely up to the caller.
//
// Styling follows the codebase convention: inline styles + theme tokens
// (var(--bg-elev), var(--border), …). Extracted from the original
// ExtensionDetailBlade so blades share one implementation.

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Button } from '@progress/kendo-react-buttons';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface BladeProps {
  /** Header title. A string is read out as the dialog's accessible name. */
  title: ReactNode;
  /** Optional secondary header line (e.g. a vendor / context label). */
  subtitle?: ReactNode;
  /** Optional leading glyph/icon in the header. */
  icon?: ReactNode;
  /** Optional right-aligned action bar pinned to the bottom (e.g. Close / Save). */
  footer?: ReactNode;
  /** Panel width; defaults to a responsive ~520px. */
  width?: number | string;
  /** Accessible label override; defaults to `title` when it's a string. */
  ariaLabel?: string;
  /** Called on backdrop click, the ✕ button, or Escape. */
  onClose: () => void;
  children: ReactNode;
}

/** Shared width for the four "AI Generate" blades (workflow, playbook, agent, extension) so they match. */
export const GENERATE_BLADE_WIDTH = 'min(680px, 94vw)';

const ANIM_MS = 180;

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.32)',
  zIndex: 1100,
};

const panelBase: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100%',
  background: 'var(--bg-elev)',
  borderLeft: '1px solid var(--border)',
  boxShadow: '-8px 0 24px rgba(0,0,0,0.18)',
  zIndex: 1101,
  display: 'flex',
  flexDirection: 'column',
  transition: `transform ${ANIM_MS}ms ease`,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '16px 20px',
  borderBottom: '1px solid var(--border)',
};

const bodyStyle: CSSProperties = {
  flex: 1,
  padding: 20,
  overflowY: 'auto',
  overflowX: 'hidden',
};

const footerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  padding: '12px 20px',
  borderTop: '1px solid var(--border)',
};

export function Blade({ title, subtitle, icon, footer, width, ariaLabel, onClose, children }: BladeProps) {
  // Drive the slide-in: mount off-screen, then transform to 0 on the next frame.
  const [shown, setShown] = useState(false);
  const panelRef = useRef<HTMLElement>(null);

  // Capture the element focused BEFORE the blade opened, at first render — this
  // runs before the panel focus-in below and before any child `autoFocus` (which
  // React applies during commit), so it's still the opener. Capturing inside an
  // effect would run post-commit, after focus already moved into the blade, and
  // restore the wrong element on close.
  const openerRef = useRef<HTMLElement | null>(null);
  if (openerRef.current === null) {
    openerRef.current = document.activeElement as HTMLElement | null;
  }

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setShown(true);
      // Move focus into the modal so keyboard users don't stay on the (now
      // inert) background — unless a child already grabbed it (e.g. autoFocus).
      const panel = panelRef.current;
      if (panel && !panel.contains(document.activeElement)) {
        const first = panel.querySelector<HTMLElement>(FOCUSABLE);
        (first ?? panel).focus();
      }
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // Restore focus to whatever was focused before the blade opened, on close.
  useEffect(() => () => openerRef.current?.focus?.(), []);

  // Close on Escape; trap Tab / Shift+Tab inside the panel (aria-modal contract).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((n) => !n.hasAttribute('disabled') && n.offsetParent !== null);
      if (focusable.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div style={backdropStyle} onClick={onClose} />
      <aside
        ref={panelRef}
        tabIndex={-1}
        style={{
          ...panelBase,
          width: width ?? 'min(520px, 92vw)',
          transform: shown ? 'translateX(0)' : 'translateX(100%)',
          outline: 'none',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
      >
        <header style={headerStyle}>
          {icon != null && <span style={{ fontSize: 22, lineHeight: 1 }}>{icon}</span>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
            {subtitle != null && <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>{subtitle}</div>}
          </div>
          <Button size="small" fillMode="flat" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </header>

        <div style={bodyStyle}>{children}</div>

        {footer != null && <footer style={footerStyle}>{footer}</footer>}
      </aside>
    </>
  );
}

export default Blade;
